<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:header>
      <div>
        <v-card-title class="screen-title"> ポリシー編集 </v-card-title>
        <v-card-subtitle class="screen-sub-title">
          UTM {{ $filter.enumTo(zone.srcZoneType, "zoneType") }}/{{
            zone.srcZoneName
          }}
          → {{ $filter.enumTo(zone.dstZoneType, "zoneType") }}/{{
            zone.dstZoneName
          }}
        </v-card-subtitle>
      </div>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="closeModal"
      >
        ×
      </button>
    </template>
    <template v-if="isLoaded" v-slot:body>
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <v-row
              no-gutters
              justify="space-between"
              class="my-2"
              align="center"
            >
              <span>{{ table.items.length }}件を表示</span>
              <v-btn color="primary" @click="addPolicy" :disabled="limitPolicy"
                >ルール追加
              </v-btn>
            </v-row>
            <app-draggable-table
              density="compact"
              :fields="table.fields"
              :items="table.items"
              primary-key="policyId"
              :hover="true"
              @click:row="editPolicy"
              :td-class="tdClass"
              :tr-class="policyRowClass"
              sticky-header="500px"
            >
              <!-- 送信元 -->
              <template #cell(srcAddress)="{ value }">
                <policy-src-address :src-address="value" />
              </template>

              <!-- 宛先 -->
              <template #cell(dstAddress)="{ value }">
                <policy-dst-address :dst-address="value" />
              </template>

              <!-- サービス -->
              <template #cell(service)="{ item }">
                <policy-service
                  :service-option="item.serviceOption"
                  :default-service-list="item.defaultServiceList"
                  :custom-service-list="item.customServiceList"
                />
              </template>

              <!-- プロファイル -->
              <template #cell(profile)="{ value }">
                <policy-profile :profile="value" />
              </template>

              <!-- アクション -->
              <template #cell(actionType)="{ value }">
                {{ $filter.enumTo(value, "allow_deny") }}
              </template>

              <!-- ログ出力 -->
              <template #cell(isLogOutput)="{ value }">
                {{ $filter.enumTo(value, "on_off") }}
              </template>

              <!-- 有効/有無 -->
              <template #cell(isPolicyStatus)="{ value }">
                {{ $filter.enumTo(value, "enable") }}
              </template>

              <!-- 備考 -->
              <template #cell(description)="{ value }">
                {{ $filter.emptyTo(value, "-") }}
              </template>

              <!-- 削除 -->
              <template #cell(delete)="{ item }">
                <v-btn
                  v-if="item.policyCategoryInfo !== 'UNEDITABLE_DEFAULT_POLICY'"
                  color="primary"
                  density="comfortable"
                  @click.stop="deletePolicy(item)"
                  >削除
                </v-btn>
              </template>
            </app-draggable-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="flat" color="primary" @click="submit">保存</v-btn>
            <v-btn variant="outlined" color="primary" @click="closeModal"
              >キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { GetPolicy, GetPolicyType1 } from "@/apis/NwPolicyApi";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import PolicySrcAddress from "@/components/policy/PolicySrcAddress.vue";
import PolicyDstAddress from "@/components/policy/PolicyDstAddress.vue";
import PolicyService from "@/components/policy/PolicyService.vue";
import PolicyProfile from "@/components/policy/PolicyProfile.vue";
import type { Type1UTMPolicy } from "@/modals/applianceContractSetting/type1/policy/InternetType1PolicyUtmList.vue";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type { Type1Service } from "@/apis/ContractChangeType1Api";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import { VueFinalModal } from "vue-final-modal";
import AppDraggableTable from "@/components/AppDraggableTable.vue";
import type { AppDraggableTableHeader } from "@/components/AppDraggableTable.vue";
import InternetType1PolicyRuleUtmEdit from "@/modals/applianceContractSetting/type1/policy/utm/InternetType1PolicyRuleUtmEdit.vue";

export default defineComponent({
  name: "InternetType1PolicyRuleUtmViewRD",
  components: {
    AppDraggableTable,
    PolicyProfile,
    PolicyService,
    PolicyDstAddress,
    PolicySrcAddress,
  },
  props: {
    /** ゾーン */
    zone: {
      type: Object as PropType<ZoneRelationItem>,
      required: true,
    },
    /** ポリシーリスト（インターネットからアクセス拠点向け/アクセス拠点からインターネット向け） */
    policyList: {
      type: Array as PropType<Type1UTMPolicy[]>,
      required: true,
    },
    /** type1ポリシー全体 */
    type1Policy: {
      type: Object as PropType<GetPolicyType1>,
      required: true,
    },
    /** サービスメニュー(インターネットType1) */
    type1Service: {
      type: Object as PropType<Type1Service>,
      required: true,
    },
  },
  data() {
    return {
      table: {
        fields: [
          { key: "_handle", title: "↑↓", width: "50px", align: "center" },
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddress", title: "送信元アドレス", width: "150px" },
          { key: "dstAddress", title: "宛先アドレス", width: "150px" },
          { key: "service", title: "サービス", width: "15%" },
          { key: "profile", title: "プロファイル", width: "20%" },
          { key: "actionType", title: "アクション", width: "80px" },
          { key: "isLogOutput", title: "ログ出力", width: "70px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考" },
          { key: "delete", title: "", width: "50px" },
        ] as AppDraggableTableHeader<Type1UTMPolicy>[],
        items: cloneDeep(this.policyList),
      },
      utmPolicyLimit: [] as UpperLimitEntity[],
      isLoaded: false,
      // 変更検知用の初期データ
      initialPolicyList: [] as Type1UTMPolicy[],
    };
  },
  computed: {
    /** 変更があるかどうかを判定 */
    hasChanges(): boolean {
      return !isEqual(this.table.items, this.initialPolicyList);
    },
    /** true: 作成できるポリシー数の上限, false: 上限ではない */
    limitPolicy(): boolean {
      // UTM側のカスタムポリシー数（ゾーン横断）
      const totalPolicy =
        (this.latestType1Policy.policyUtmType1?.accessPointToInternetList
          .flatMap((e) => e.policyList)
          .filter((e) => e.policyCategoryInfo === "CUSTOM_POLICY").length ??
          0) +
        (this.latestType1Policy.policyUtmType1?.internetToAccessPointList
          .flatMap((e) => e.policyList)
          .filter((e) => e.policyCategoryInfo === "CUSTOM_POLICY").length ?? 0);
      return (
        totalPolicy >=
        this.utmPolicyLimit!.filter(
          (e) => e.upperLimitItemName === "ポリシー数(UTM)"
        )[0]!.upperLimit
      );
    },
    /** 最新の全体ポリシー（全体ポリシー + 編集中のポリシー ）*/
    latestType1Policy(): GetPolicyType1 {
      return {
        ...this.type1Policy,
        policyUtmType1: {
          internetToAccessPointList:
            this.type1Policy.policyUtmType1?.internetToAccessPointList.map(
              (e) => {
                return e.zoneRelationId === this.zone.zoneRelationId
                  ? {
                      ...e,
                      policyList: this.table.items as typeof e.policyList,
                    }
                  : e;
              }
            ) ?? [],
          accessPointToInternetList:
            this.type1Policy.policyUtmType1?.accessPointToInternetList.map(
              (e) => {
                return e.zoneRelationId === this.zone.zoneRelationId
                  ? {
                      ...e,
                      policyList: this.table.items as typeof e.policyList,
                    }
                  : e;
              }
            ) ?? [],
        },
      };
    },
  },
  async mounted() {
    // 初期データを保存
    this.initialPolicyList = cloneDeep(this.policyList);
    /** UTMポリシー設定上限値の取得 */
    this.utmPolicyLimit = (
      await this.$api.upperLimit.getUpperLimitLine({
        upperLimitLineManageUnit: "APPLIANCE",
        applianceType: "UTM",
        enumber: this.type1Service.enumberMainAct,
      })
    ).internetLine!.upperLimitList!;
    this.isLoaded = true;
  },
  methods: {
    tdClass(field: AppDraggableTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
    /** ポリシー追加 */
    async addPolicy() {
      const addPolicy: Type1UTMPolicy = await this.$modal.show(
        InternetType1PolicyRuleUtmEdit,
        {
          zone: this.zone,
          type1Policy: this.latestType1Policy,
        }
      );
      const last = this.table.items
        .map((e) => e.policyCategoryInfo)
        .lastIndexOf("UNEDITABLE_DEFAULT_POLICY");
      this.table.items.splice(
        // 最後 or 編集不可の一つ前
        last === -1 ? this.table.items.length : last,
        0,
        addPolicy
      );
    },

    /** ポリシー編集 */
    async editPolicy(policy: Type1UTMPolicy) {
      if (policy.policyCategoryInfo === "UNEDITABLE_DEFAULT_POLICY") {
        return;
      }
      const editPolicy: Type1UTMPolicy = await this.$modal.show(
        InternetType1PolicyRuleUtmEdit,
        {
          zone: this.zone,
          policy,
          type1Policy: this.latestType1Policy,
        }
      );
      const index = this.table.items.indexOf(policy);
      this.table.items[index] = editPolicy;
    },

    /** ポリシー削除 */
    async deletePolicy(item: Type1UTMPolicy) {
      const index = this.table.items.indexOf(item);
      this.table.items.splice(index, 1);
    },

    /** ポリシー編集完了 */
    async submit() {
      (
        this.$refs.modal as typeof VueFinalModal & { ok: (e: unknown) => void }
      ).ok(this.table.items);
    },
    /** ×ボタン、キャンセルボタンクリック時の処理 */
    async closeModal() {
      // 変更がない場合はそのまま閉じる
      if (!this.hasChanges) {
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
        return;
      }

      try {
        await this.$confirm("変更を破棄してキャンセルします、よろしいですか？");
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
      } catch (error) {
        // キャンセルが押された場合：何もしない
        return;
      }
    },

    /** ポリシーテーブルのtrクラス. */
    policyRowClass(
      item: Pick<GetPolicy, "policyCategoryInfo">
    ): string[] | undefined {
      switch (item.policyCategoryInfo) {
        case "CUSTOM_POLICY":
          // ドラッグ可能
          return ["draggable", "clickable"];
        case "EDITABLE_DEFAULT_POLICY":
          // ドラッグ可能
          return ["draggable", "clickable"];
        case "UNEDITABLE_DEFAULT_POLICY":
          // ドラッグ不可 + 背景色グレーアウト
          return ["gray-out"];
      }
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(th) {
  font-size: 90%;
}
</style>
